import { defineStore } from "pinia"
import {
  CASELOAD_QUERY,
  CASELOAD_UNREAD_QUERY,
  TRANSFER_CASE_MUTATION,
  UPDATE_CASE_DETAILS_MUTATION,
  UPDATE_CASE_STAFF_MUTATION,
} from "~/graphql/cases/caseload"
import {
  SET_DIAGNOSTICS_AND_INSURANCE_INFO_MUTATION,
  SOFT_REMOVE_CASE_MUTATION,
  DELETE_CASE_PERMANENTLY_MUTATION,
} from "~/graphql/cases/mutations"
import {
  CASE_FILTER_KEYS,
  filterCompanyCases,
  hasActiveCaseFilters,
  makeCaseFilterDefaults,
  makePlanFilterDefault,
} from "~/utils/companyCases"
import { toIntOrNull } from '~/utils/number'
import { afterCaseErased } from '~/utils/caseSelection'
import { hasKnownErrorCode } from '~/utils/errorMessages'
import { useApolloClient } from "@vue/apollo-composable"
import { getDaysDifference } from "~/utils/date"
import { activeCasesKey, createPrefetchSlot } from "~/utils/companyCasesArrival"
import {
  invalidateCaseNav,
  useCaseNavigation,
} from "@/composables/useCaseNavigation"
import { useFollowupsViewMemory } from "@/composables/useFollowupsViewMemory"
import {
  readViewAsUserId,
} from "@/composables/usePlanningImportScope"

// ── Client-side case filters ────────────────────────────────────────────────
// The planning DAY (`planFilter`, level 2) is kept separate from the toolbar
// FILTERS (level 3): each day view within a (company, lead agency) keeps its
// own filters. Both persist via the unified `useFollowupsViewMemory`.

// One in-flight arrival prefetch of the caseload (utils/companyCasesArrival).
const activeCasesPrefetch = createPrefetchSlot()

// The toolbar filters + their defaults, and the planning day's default, live in
// utils/companyCases (one list: state, snapshot, restore and reset all use it).
const makePlanDefault = makePlanFilterDefault
const makeFilterDefaults = makeCaseFilterDefaults

export const FILTER_KEYS = CASE_FILTER_KEYS

const viewMemory = useFollowupsViewMemory()

export const useCasesStore = defineStore("casesStore", {
  id: "casesStore",
  state: () => ({
    activeCases: [],
    activeCasesLoading: false,
    selectedCase: null,

    // Set when a case was just ERASED: the case page leaves its pane empty
    // instead of selecting the neighbour (utils/caseSelection `afterCaseErased`).
    // Cleared when the user picks a case, arrives, or switches company / agency.
    selectionHeldEmpty: false,

    // Bumped on every local plan set/move/remove (applyCasePlan) so cross-company
    // views — e.g. the planning bar's per-day TOTAL load — can refetch the agenda.
    planMutationRev: 0,

    // The (company, lead agency) whose cases are loaded — the scope under which
    // the planning day + filters persist. Set by `restoreFilters` /
    // `fetchActiveCasesForCompany`.
    _currentCompanyId: null,
    _currentLeadAgencyId: null,

    // Planning day (level 2): {kind:'all'|'day'|'future', iso}.
    // Kept SEPARATE from the toolbar filters so each day keeps its own filters.
    planFilter: makePlanDefault(),

    // Toolbar filters (level 3). Defaults + canonical list in `makeFilterDefaults`:
    //   filterCasesInsuranceId / ...StageTagId — null (off) | 'any' | '<id>'
    //   filterCasesWithUnreadFollowUps — bool
    ...makeFilterDefaults(),
  }),
  getters: {
    /**
     * Reactive snapshot of the current filters, keyed exactly like the
     * defaults. A getter (not an action) so a `watch` on it actually tracks
     * the underlying filter state — that's what drives the per-company
     * persistence in `followups.vue`.
     */
    filtersSnapshot(state) {
      const snap = {}
      for (const key of FILTER_KEYS) snap[key] = state[key]

      return snap
    },

    /**
     * Whether any toolbar filter (level 3) is active — i.e. differs from its
     * default. Defaults are `false` (booleans) / `null` (id filters), so a
     * value that's neither counts as active. Drives the case-list empty state:
     * "no cases match the filters" vs an empty planning day. The plan DAY
     * (`planFilter`) is deliberately NOT counted here.
     */
    hasActiveToolbarFilters(state) {
      return hasActiveCaseFilters(state)
    },

    /**
     * Active cases after optional client-side checklist filters (AND when both on).
     * Sorted by startedAt ascending — oldest first, so the sidebar shows
     * the longest-running cases at the top. Cases with no startedAt sink
     * to the bottom rather than mixing in unpredictably.
     */
    displayedActiveCases(state) {
      return filterCompanyCases(state.activeCases, state, state.planFilter)
    },
  },
  actions: {
    /**
     * Reset every client-side filter to its default (show-all) state.
     * Used when a deep-link / search points at a case that exists in the
     * active set but is hidden by an active filter — we clear the filters
     * so the requested case becomes visible and selectable.
     */
    resetCaseFilters() {
      this.planFilter = makePlanDefault()
      this.resetToolbarFilters()
    },

    /**
     * Clear only the toolbar filters (level 3), keeping the planning day.
     * Used when a deep-link already landed on the case's planning day and
     * a leftover per-day filter is the only thing hiding it.
     */
    resetToolbarFilters() {
      Object.assign(this, makeFilterDefaults())
    },

    /**
     * Persist the current filters (level 3) keyed by (company, lead agency,
     * planning day). Self-contained: uses the scope set by `restoreFilters` /
     * `fetchActiveCasesForCompany`, so callers just say "save now".
     */
    persistFilters() {
      if (this._currentCompanyId == null) return

      viewMemory.rememberFilters(
        this._currentCompanyId,
        this._currentLeadAgencyId,
        this.planFilter,
        { ...this.filtersSnapshot },
      )
    },

    /**
     * Restore the saved view for a (company, lead agency): the planning day
     * (level 2), then that day's filters (level 3) — defaults when none saved.
     * Remembers the scope so later `persistFilters` / `setPlanFilter` write to
     * the right keys. The CasesFilterBar auto-clear watchers then drop any
     * restored filter whose target has no matching cases in the loaded set.
     */
    restoreFilters(companyId, leadAgencyId) {
      if (companyId == null) return
      this._currentCompanyId = String(companyId)
      this._currentLeadAgencyId =
        leadAgencyId != null ? String(leadAgencyId) : null

      // Level 2 — planning day for this (company, lead agency).
      const day = viewMemory.recallPlanningDay(companyId, leadAgencyId)

      this.planFilter = day && typeof day === "object" ? day : makePlanDefault()

      // Level 3 — filters for this (company, lead agency, planning day).
      this._applyFiltersForCurrentDay()
    },

    /** Reapply the saved filters for the current (company, LA, day), or defaults. */
    _applyFiltersForCurrentDay() {
      const saved = viewMemory.recallFilters(
        this._currentCompanyId,
        this._currentLeadAgencyId,
        this.planFilter,
      )

      const defaults = makeFilterDefaults()
      for (const key of FILTER_KEYS) {
        this[key] = saved && saved[key] !== undefined ? saved[key] : defaults[key]
      }
    },

    /**
     * Set the planning day (level 2) and swap in that day's saved filters
     * (level 3). The planning bar calls this instead of assigning `planFilter`
     * directly, so the day is remembered and per-day filters apply.
     */
    setPlanFilter(filter) {
      this.planFilter =
        filter && typeof filter === "object" ? filter : makePlanDefault()

      if (this._currentCompanyId == null) return
      viewMemory.rememberPlanningDay(
        this._currentCompanyId,
        this._currentLeadAgencyId,
        this.planFilter,
      )
      this._applyFiltersForCurrentDay()
    },

    /**
     * Refetch active cases using current company, lead agency, and plan-day filter.
     */
    async refetchActiveCasesForCurrentFilters() {
      const nav = useCaseNavigation()
      const companyId = nav.selectedCompany.value?.id
      if (!companyId) return

      // Day filtering is client-side: always load the full active set so the
      // filter-bar buckets and the in-memory filter stay correct.
      await this.fetchActiveCasesForCompany(companyId, {
        leadAgencyId: nav.selectedLeadAgency.value?.id ?? null,
      })
    },

    /**
     * Patch a single case's `myPlan` (the current user's planned day) in
     * place after a set/move/remove, so the day badge and the (future)
     * day filter recompute instantly without a refetch.
     * @param {string|number} caseId
     * @param {{ id: string|number, plannedFor: string }|null} plan
     */
    applyCasePlan(caseId, plan) {
      const target = String(caseId)

      const next = plan
        ? { id: String(plan.id), plannedFor: plan.plannedFor }
        : null

      this._patchCase(target, { myPlan: next })
      this.planMutationRev += 1
    },

    /**
     * Patch a single case's `tags` in place after assigning/removing a tag,
     * so the stage-tag filter (and any tag-driven view) recomputes instantly
     * without a refetch. Mirrors `applyCasePlan` / the plan-badge sync:
     * both the active-cases entry AND the selected case are updated, so a
     * filtered list never keeps showing a case whose tags no longer match.
     * @param {string|number} caseId
     * @param {Array<{ tag: object }>} tags - the case's full tag list.
     */
    applyCaseTags(caseId, tags) {
      this._patchCase(caseId, { tags: Array.isArray(tags) ? tags : [] })
    },

    /**
     * Patch a case's assignee in place (after `assignCaseToUser`) so chips and
     * the "assigned to" filter recompute without a refetch. Mirrors
     * `applyCaseTags`.
     */
    applyCaseAssignee(caseId, assignedTo) {
      this._patchCase(caseId, { assignedTo: assignedTo ?? null })
    },

    /**
     * Merge `fields` into one case — its active-list entry AND the selected
     * case, so a filtered list never keeps showing a case whose data no longer
     * matches. Shared by the plan / tags / assignee patches.
     */
    _patchCase(caseId, fields) {
      const target = String(caseId)

      const idx = this.activeCases.findIndex(c => String(c.id) === target)
      if (idx !== -1) {
        this.activeCases[idx] = { ...this.activeCases[idx], ...fields }
      }

      if (this.selectedCase && String(this.selectedCase.id) === target) {
        this.selectedCase = { ...this.selectedCase, ...fields }
      }
    },

    /**
         * Fetch active cases for a company with given filters.
     * @param {*} companyId
     * @param {*} filters
     */
    /**
     * Start loading a company's active caseload NOW, before the page has
     * selected that scope (the /company-cases arrival knows it from the server
     * while the company bar is still loading). The next
     * `fetchActiveCasesForCompany` for the same scope takes this request
     * instead of sending its own.
     */
    prefetchActiveCases(companyId, leadAgencyId = null) {
      if (companyId == null) return

      const variables = {
        companyId: String(companyId),
        filters: { caseStageCategory: "active", leadAgencyId: leadAgencyId != null ? String(leadAgencyId) : null },
        viewAsUserId: readViewAsUserId(),
      }

      const { resolveClient } = useApolloClient()
      const promise = resolveClient().query({ query: CASELOAD_QUERY, variables, fetchPolicy: "network-only" })

      promise.catch(() => {}) // a failure is handled by whoever takes it
      activeCasesPrefetch.put(activeCasesKey({
        companyId,
        leadAgencyId,
        caseStageCategory: "active",
        viewAsUserId: variables.viewAsUserId,
      }), promise)
    },

    async fetchActiveCasesForCompany(companyId, filters) {
      try {
        this.activeCasesLoading = true

        // Keep the persistence scope in sync even when callers fetch without
        // going through `restoreFilters` first.
        if (companyId != null) {
          this._currentCompanyId = String(companyId)
          this._currentLeadAgencyId =
            filters?.leadAgencyId != null ? String(filters.leadAgencyId) : null
        }

        // Only forward the fields the backend CaseFiltersInput accepts —
        // drops any stray planningTagId from older callers.
        const filtersForQuery = {
          caseStageCategory: filters?.caseStageCategory ?? "active",
          leadAgencyId: filters?.leadAgencyId ?? null,
        }


        // "View As": the backend resolves each case's `myPlan` for the viewed
        // user (read-only) when `viewAsUserId` is set — no client-side patch.
        const variables = {
          companyId,
          filters: filtersForQuery,
          viewAsUserId: readViewAsUserId(),
        }

        const { resolveClient } = useApolloClient()
        const client = resolveClient()

        const fetchNow = () => client.query({
          query: CASELOAD_QUERY,
          variables,
          fetchPolicy: "network-only",
        })

        // The arrival may already have this exact request in flight (see
        // `prefetchActiveCases`) — take it; a failed prefetch just refetches.
        const prefetched = activeCasesPrefetch.take(activeCasesKey({
          companyId,
          leadAgencyId: filtersForQuery.leadAgencyId,
          caseStageCategory: filtersForQuery.caseStageCategory,
          viewAsUserId: variables.viewAsUserId,
        }))

        const { data } = await (prefetched ? prefetched.catch(fetchNow) : fetchNow())

        const result = data?.getCasesForCompany || []

        this.activeCases = result.map(caseItem => ({
          ...caseItem,
          dateDiff: getDaysDifference(
            new Date(),
            new Date(caseItem.startedAt || caseItem.createdAt),
          ),
        }))
      } catch (err) {
        console.error("Error getting the planned active cases: ", err)
      } finally {
        this.activeCasesLoading = false
      }
    },

    /**
         * Fetch live updates for active cases.
     * @returns
     */
    async fetchActiveCasesLiveUpdates() {
      try {
        const nav = useCaseNavigation()

        const filters = {
          caseStageCategory: "active",
        }

        const companyId = nav.selectedCompany.value?.id
        if (!companyId) return

        const selectedLeadAgencyId = nav.selectedLeadAgency.value?.id
        if (selectedLeadAgencyId) {
          filters.leadAgencyId = selectedLeadAgencyId
        }

        // No plan-day filter here: live updates must refresh the
        // full active set (matched by id below) so every chip's unread
        // count stays fresh, not just the currently-selected week's.

        const filtersForQuery = filters


        const variables = { companyId, filters: filtersForQuery }

        // `useAsyncQuery` is SSR-aware and was returning stale (or
        // empty) data when invoked from a Pinia action post-hydration
        // — switching to the imperative client.query keeps the
        // per-case `unreadTrackingCount` in sync after a follow-up
        // read. Without this the case-list badge could be stuck on
        // its pre-read value while the navbar count had already
        // updated.
        const { resolveClient } = useApolloClient()
        const client = resolveClient()

        const { data } = await client.query({
          query: CASELOAD_UNREAD_QUERY,
          variables,
          fetchPolicy: "network-only",
        })

        const result = data?.getCasesForCompany || []

        const liveData = result.map(caseItem => ({ ...caseItem }))

        liveData.forEach(liveCase => {
          const caseIndex = this.activeCases.findIndex(
            c => c.id === liveCase.id,
          )

          if (caseIndex !== -1) {
            this.activeCases[caseIndex].unreadTrackingCount =
              liveCase.unreadTrackingCount

            // also update selected case if it is the same
            if (this.selectedCase && this.selectedCase.id === liveCase.id) {
              this.selectedCase.unreadTrackingCount =
                liveCase.unreadTrackingCount
            }
          }
        })
      } catch (err) {
        console.error("getActiveCasesLiveUpdates: ", err)
      }
    },

    /**
     * Decrement the selected case's unread badge by one (after marking a
     * follow-up read).
     */
    async decreaseUnreadFollowUpsCount() {
      const selectedCaseId = this.selectedCase?.id
      if (selectedCaseId != null) {
        const caseIndex = this.activeCases.findIndex(
          c => c.id === selectedCaseId,
        )

        if (caseIndex !== -1) {
          const current = this.activeCases[caseIndex].unreadTrackingCount || 0

          this.activeCases[caseIndex].unreadTrackingCount = Math.max(0, current - 1)
        }
      }
    },

    /**
     * Replace a case's checklist in the loaded list (and the store's selected
     * case) so the card list stays coherent after a chip click.
     * @param {string} caseId
     * @param {Array} checklist
     */
    setCaseChecklist(caseId, checklist) {
      const target = this.activeCases.find(c => String(c.id) === String(caseId))
      if (target) target.checklist = checklist
      if (this.selectedCase && String(this.selectedCase.id) === String(caseId))
        this.selectedCase.checklist = checklist
    },

    /**
     * Soft-remove a case. Cascades removed=true to all its follow-ups and cleans tracking records.
     * Returns true on success.
     * @param {string} caseId
     */
    async softRemoveCase(caseId) {
      try {

        const { resolveClient } = useApolloClient()
        const client = resolveClient()

        const { data } = await client.mutate({
          mutation: SOFT_REMOVE_CASE_MUTATION,
          variables: { caseId },
        })

        if (data?.softRemoveCase === true) {
          this.activeCases = this.activeCases.filter(c => c.id !== caseId)
          if (this.selectedCase?.id === caseId) this.selectedCase = null
          invalidateCaseNav('case-remove').catch(() => {})

          return true
        }
        
        return false
      } catch (err) {
        console.error("Error soft-removing case:", err)
        throw err
      }
    },

    /**
     * Permanently delete a case — erased from the database (NEW CASES only; the
     * server refuses anything else). Drops it from the loaded list and the nav counts.
     * Returns true on success.
     * @param {string} caseId
     */
    async deleteCasePermanently(caseId) {
      const { resolveClient } = useApolloClient()

      const { data } = await resolveClient().mutate({
        mutation: DELETE_CASE_PERMANENTLY_MUTATION,
        variables: { caseId },
      })

      if (data?.deleteCasePermanently !== true) return false

      // The page keeps its pane EMPTY afterwards instead of advancing to the
      // neighbouring case — `afterCaseErased` says why.
      const after = afterCaseErased(this.activeCases, this.selectedCase, caseId)

      this.selectionHeldEmpty = after.holdSelectionEmpty
      this.activeCases = after.activeCases
      this.selectedCase = after.selectedCase
      invalidateCaseNav('case-remove').catch(() => {})

      return true
    },

    /**
         * Function to update a case along with its diagnostics and insurances.
     * @param {*} caseData
     * @param {*} diagnosticsToUpdate
     * @param {*} insurancesToUpdate
     * @returns
     */
    async updateCase(caseData, diagnosticsToUpdate, insurancesToUpdate) {
      try {

        // leadSource may arrive as the relation object (unchanged) or as a
        // raw id (after the user picks a new one in the select).
        const leadSourceId =
          caseData.leadSource && typeof caseData.leadSource === 'object'
            ? caseData.leadSource.id
            : caseData.leadSource

        const variables = {
          caseId: caseData.id,
          data: {
            motherName: caseData.motherName,
            fatherName: caseData.fatherName,
            otherContactName: caseData.otherContactName,
            contactLanguage: caseData.contactLanguage,

            contactPhone: caseData.contactPhone,
            contactEmail: caseData.contactEmail,

            homeAddress: caseData.homeAddress,
            homeCity: caseData.homeCity,
            homeState: caseData.homeState,
            homeZipCode: caseData.homeZipCode,

            schoolName: caseData.schoolName,

            schoolAddress: caseData.schoolAddress,
            schoolCity: caseData.schoolCity,
            schoolState: caseData.schoolState,
            schoolZipCode: caseData.schoolZipCode,

            // The therapy window — "HH:MM", or "" when nobody set it.
            therapyStartTime: caseData.therapyStartTime ?? '',
            therapyEndTime: caseData.therapyEndTime ?? '',

            // The week in words, and whatever qualifies it — the clock pair
            // above is one window; these two are the shape of the week.
            availabilityHours: toIntOrNull(caseData.availabilityHours),
            availabilityNotes: caseData.availabilityNotes ?? '',

            childName: caseData.childName,
            childDateOfBirth: caseData.childDateOfBirth,
            childGender: caseData.childGender,
            childLanguage: caseData.childLanguage,

            caseType: caseData.caseType,
            therapyPlace: caseData.therapyPlace,
            otherTherapy: caseData.otherTherapy,

            startedAt: caseData.startedAt,

            generalNotes: caseData.generalNotes,
            caseInsurancesNotes: caseData.caseInsurancesNotes,
            insuranceMemberId: caseData.insuranceMemberId,
            caseDiagnosticsNotes: caseData.caseDiagnosticsNotes,
            initialDocuments: caseData.initialDocuments,

            ...(leadSourceId != null
              ? { leadSource: { connect: { id: leadSourceId } } }
              : {}),
          },
        }

        const { mutate } = useMutation(UPDATE_CASE_DETAILS_MUTATION, { variables })

        const updateResult = await mutate()

        const updatedCase = updateResult?.data?.updateCase
        if (updatedCase) {
          this.selectedCase = {
            ...this.selectedCase,
            ...updatedCase,
          }


          const variables = {
            caseId: caseData.id,
            diagnostics: diagnosticsToUpdate,
            insurances: insurancesToUpdate,
          }

          const { mutate } = await useMutation(SET_DIAGNOSTICS_AND_INSURANCE_INFO_MUTATION, {
            variables,
          })

          const result = await mutate()

          if (result?.data?.setDiagnosticsAndInsuranceInfo) {
            this.selectedCase = {
              ...this.selectedCase,
              ...result.data.setDiagnosticsAndInsuranceInfo,
            }
          }

          const caseIndex = this.activeCases.findIndex(
            c => c.id === this.selectedCase.id,
          )

          if (caseIndex !== -1) {
            this.activeCases[caseIndex] = {
              ...this.activeCases[caseIndex],
              ...this.selectedCase,
            }
          }
        }

        return true
      } catch (err) {
        console.error("Error updating case:", err)
      }
    },

    /**
     * Transfer a case to a different company.
     * Removes the case from the current active list after success.
     */
    async transferCaseToCompany(caseId, newCompanyId) {
      try {

        const variables = { caseId, companyId: newCompanyId }
        const { mutate } = useMutation(TRANSFER_CASE_MUTATION, { variables })
        const result = await mutate()

        if (result?.data?.updateCase) {
          this.activeCases = this.activeCases.filter(c => c.id !== caseId)
          if (this.selectedCase?.id === caseId) this.selectedCase = null
          invalidateCaseNav('case-transfer').catch(() => {})

          return true
        }

        return false
      } catch (err) {
        console.error("Error transferring case:", err)

        return false
      }
    },

    /**
     * Update staff assignments (BCBA, BCaBA, RBT) and notes for a case.
     * Each id may be a string/number to assign or null to disconnect.
     *
     * @param {string|number} caseId
     * @param {{ bcbaId: string|number|null, bcabaId: string|number|null, rbtId: string|number|null, bcbaNotes: string, rbtNotes: string }} payload
     * @returns {Promise<boolean>}
     */
    async updateStaffForCase(caseId, { bcbaId, bcabaId, rbtId, bcbaNotes, rbtNotes, bcbaDriveTime, bcabaDriveTime, rbtDriveTime }) {
      try {

        // Map a staff id (or null) to a Keystone relationship update payload.
        const relationUpdate = id =>
          id == null ? { disconnect: true } : { connect: { id: String(id) } }

        const variables = {
          caseId,
          data: {
            bcba: relationUpdate(bcbaId),
            bcaba: relationUpdate(bcabaId),
            rbt: relationUpdate(rbtId),
            bcbaNotes: bcbaNotes == null ? "" : bcbaNotes,
            rbtNotes: rbtNotes == null ? "" : rbtNotes,
            bcbaDriveTime: bcbaDriveTime ?? null,
            bcabaDriveTime: bcabaDriveTime ?? null,
            rbtDriveTime: rbtDriveTime ?? null,
          },
        }

        const { mutate } = await useMutation(UPDATE_CASE_STAFF_MUTATION, {
          variables,
        })

        const result = await mutate()

        const updatedCase = result?.data?.updateCase
        if (updatedCase) {
          if (this.selectedCase && this.selectedCase.id === updatedCase.id) {
            this.selectedCase.bcba = updatedCase.bcba
            this.selectedCase.bcaba = updatedCase.bcaba
            this.selectedCase.rbt = updatedCase.rbt
            this.selectedCase.bcbaNotes = updatedCase.bcbaNotes
            this.selectedCase.rbtNotes = updatedCase.rbtNotes
            this.selectedCase.bcbaDriveTime = updatedCase.bcbaDriveTime
            this.selectedCase.bcabaDriveTime = updatedCase.bcabaDriveTime
            this.selectedCase.rbtDriveTime = updatedCase.rbtDriveTime
          }

          const caseIndex = this.activeCases.findIndex(
            c => c.id === updatedCase.id,
          )

          if (caseIndex !== -1) {
            this.activeCases[caseIndex].bcba = updatedCase.bcba
            this.activeCases[caseIndex].bcaba = updatedCase.bcaba
            this.activeCases[caseIndex].rbt = updatedCase.rbt
            this.activeCases[caseIndex].bcbaNotes = updatedCase.bcbaNotes
            this.activeCases[caseIndex].rbtNotes = updatedCase.rbtNotes
            this.activeCases[caseIndex].bcbaDriveTime = updatedCase.bcbaDriveTime
            this.activeCases[caseIndex].bcabaDriveTime = updatedCase.bcabaDriveTime
            this.activeCases[caseIndex].rbtDriveTime = updatedCase.rbtDriveTime
          }
        }

        return true
      } catch (err) {
        console.error("Error updating staff for case:", err)

        // A known refusal (e.g. CASE_STAFF_NOT_ACTIVE) reaches the modal, which
        // shows its friendly message; anything else stays the generic failure.
        if (hasKnownErrorCode(err)) throw err

        return false
      }
    },
  },
})
